import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OfficerTypeEnum, SupportedCountryType } from '@generated/graphql';
import { map, Observable, startWith } from 'rxjs';
import { ExistsOfficerType } from '@shared/component/job/component/company-incorporation/dialog/add-officer-dialog/classes/exists-officer.type';

@Component({
  selector: 'app-exists-officers-select',
  templateUrl: './exists-officers-select.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatInputModule,
  ],
})
export class ExistsOfficersSelectComponent implements OnInit, OnChanges {
  @Input() existingOfficers: ExistsOfficerType[] = [];
  @Input() isShareholder = false;
  @Input() isDirector = false;
  @Input() selectedOfficerId: string | null = null;
  @Input() countryOfIncorporation: SupportedCountryType;
  @Input() excludedOfficerIds: string[] = [];

  @Output() existingOfficerSelected = new EventEmitter<
    ExistsOfficerType | undefined
  >();

  officerFilterControl = new FormControl('', { nonNullable: true });

  filteredOfficers$: Observable<ExistsOfficerType[]>;

  get officerRoleLabel(): string {
    if (this.isShareholder) {
      return 'shareholder';
    }

    if (this.isDirector) {
      return 'director';
    }

    return 'officer';
  }

  ngOnInit(): void {
    this.filteredOfficers$ = this.officerFilterControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterExistingOfficers(value)),
    );

    this.syncSelectedOfficer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedOfficerId'] || changes['existingOfficers']) {
      this.syncSelectedOfficer();
    }
  }

  private syncSelectedOfficer(): void {
    if (!this.selectedOfficerId) {
      return;
    }

    this.officerFilterControl.setValue(this.selectedOfficerId, {
      emitEvent: false,
    });
    this.officerFilterControl.updateValueAndValidity();
  }

  private filterExistingOfficers(value: string): ExistsOfficerType[] {
    const filterValue = value.toLowerCase();

    return this.existingOfficers
      .filter((o) => !this.excludedOfficerIds.includes(o.id))
      .filter((officer) => officer.name.toLowerCase().includes(filterValue));
  }

  getDisplayExistingOfficerName = (officerId: string): string => {
    const officer = this.existingOfficers.find((o) => o.id === officerId);

    if (!officer) {
      return '';
    }

    return officer.name;
  };

  onExistingOfficerSelected({
    option,
  }: MatAutocompleteSelectedEvent): void {
    const id = option.value;

    const officer = this.existingOfficers.find((o) => o.id === id);

    this.existingOfficerSelected.emit(officer);
  }

  isIndividual(officer: ExistsOfficerType): boolean {
    return officer.type === OfficerTypeEnum.Individual;
  }

  isCorporate(officer: ExistsOfficerType): boolean {
    return officer.type === OfficerTypeEnum.Corporate;
  }

  isDisabled(id: string): boolean {
    return this.excludedOfficerIds.includes(id);
  }
}
